import TermsOfUseIcon from '../../assets/icon-terms-of-use.svg?react';
import UserIcon from '../../assets/icon-user.svg?react';
import CategoryIcon from '../../assets/icon-category.svg?react';
import CheckIcon from '../../assets/icon-google-tick.svg?react';
import IconBell from '../../assets/icon_bell.svg?react';
import CloseIcon from '../../assets/icon-close.svg?react';
import { Modal, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { HISTORY_ZERO_STATE } from '@/constants/HistoryConstants';
import useElysiaStore from '@/app/elysia-store';
import { splitByWords } from '@/utils/titleUtils';
import useIsMobile from '../../utils/useIsMobile';
import FilledHeartIcon from '@/assets/icon-heart-filled.svg?react';
import EmptyHeartIcon from '@/assets/icon-heart-outline.svg?react';
import { toggleAppFavoriteStatus } from '@/services/aiApps';

interface SummaryModalProps {
  cardData: any;
  showModal: any;
  setShowSummaryModal: any;
  tabName: any;
}

export const SummaryModal = ({
  cardData,
  showModal,
  setShowSummaryModal,
  tabName,
}: SummaryModalProps) => {
  const closeModalRef = useRef<HTMLButtonElement>(null);
  const launchButtonRef = useRef<HTMLButtonElement>(null);
  const setAppFavourite = useElysiaStore((state) => state.setAppFavourite);
  const favoriteList = useElysiaStore((state) => state.favouriteAgent);
  const [markFavourite, setMarkFavourite] = useState(() => {
    const favItem = favoriteList?.find((ele) => ele.agent_id === cardData?.agent_id);
    return favItem?.is_favourite ?? false;
  });
  const setActiveChatHistoryRecord = useElysiaStore.getState().setActiveChatHistoryRecord;
  const setIsPrivateChat = useElysiaStore.getState().setIsPrivateChat;
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setBrandDoc = useElysiaStore.getState().setBrandDoc;
  const resetAIApp = useElysiaStore.getState().resetAIApp;
  const setIsAppTerminated = useElysiaStore.getState().setIsAppTerminated;
  const isMobile = useIsMobile(767);

  const handleLaunchKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      closeModalRef.current?.focus();
    }
  };
  const handleCloseKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab' && event.shiftKey && launchButtonRef.current) {
      event.preventDefault();
      launchButtonRef.current?.focus();
    }
  };

  const navigate = useNavigate();
  const handleLaunchApp = () => {
    setBrandDoc?.(false);
    setIsPrivateChat?.(false);
    setActiveChatHistoryRecord(HISTORY_ZERO_STATE);
    setChatAgent?.('app-agent');
    setIsAppTerminated(false);
    resetAIApp();
    const url = cardData?.redirect_url
      ? `${cardData.redirect_url}/?app=${cardData.agent_id}`
      : `/?app=${cardData.agent_id}`;

    navigate(url, { state: { agent_id: cardData.agent_id } });
  };

  useEffect(() => {
    if (showModal) {
      setTimeout(() => {
        if (closeModalRef.current) {
          closeModalRef.current.focus();
        }
      }, 0);
    }
  }, [showModal]);

  const handleToggleFavourite = async () => {
    const payload = { agent_id: cardData?.agent_id, is_favourite: !markFavourite };
    setMarkFavourite(!markFavourite);
    const response = await toggleAppFavoriteStatus(payload);
    if (response) {
      setAppFavourite(payload);
    }
  };
  return (
    <>
      <Modal
        show={showModal}
        onHide={() => setShowSummaryModal(false)}
        centered
        dialogClassName={`summary-modal ${isMobile && 'summary-modal-mobile'} `}
      >
        <Modal.Header className="border-0 p-0 d-flex align-items-center justify-content-between w-100">
          <Modal.Title className="mb-lg-0 horizontal-alignments-mobile ">
            <div className="d-flex align-items-center">
              {cardData?.icon && (
                <img src={cardData?.icon} className="app-icon me-2" alt="ai-app-icon" />
              )}
              <h5 className="mb-0" title={splitByWords(cardData?.label, 10)}>
                {cardData.label}
              </h5>
            </div>
          </Modal.Title>

          <Button
            ref={closeModalRef}
            className={`close-btn ${isMobile ? 'p-3 me-2' : 'p-0'}`}
            onClick={() => setShowSummaryModal(false)}
            onKeyDown={handleCloseKeyDown}
            aria-label="Close summary dialog"
          >
            <CloseIcon />
          </Button>
        </Modal.Header>

        <Modal.Body className="p-0 ">
          {/* Adding Conditional Notification Icon */}

          {cardData?.agent_status !== 'published' && (
            <div
              className={`notification-tooltip d-flex mb-2.5 h-auto ${isMobile && 'horizontal-alignments-mobile'}`}
            >
              <IconBell className="flex-shrink-0 bell-icon" />
              {cardData?.agent_status == 'unpublished' && tabName === 'My AI Apps' ? (
                <p className="mb-0">This app is not published yet.</p>
              ) : tabName === 'My AI Apps' && cardData?.agent_status == 'processing' ? (
                <div className="mb-0">
                  Usage and Sharing is prevented for Apps while their Knowledge files are uploaded.
                  You will receive an email when this process is complete
                </div>
              ) : (
                cardData?.agent_status === 'unpublished' && (
                  <p className="mb-0">This app is currently disabled by the owner.</p>
                )
              )}
            </div>
          )}

          <div
            className={`${isMobile ? 'horizontal-alignments-mobile' : 'card-data-descritption'}`}
            title={cardData?.description && splitByWords(cardData?.description, 5)}
          >
            {cardData?.description}
          </div>

          <div
            className={`agent-app-info d-flex align-items-center my-4 ${isMobile && 'horizontal-alignments-mobile'}`}
          >
            <div
              className={`agent-info-item d-flex ${cardData?.label == 'Brand Agent' ? 'me-5' : ''}`}
            >
              <CategoryIcon className="category-icon me-2" />
              <div>
                <div className="app-info-text" title={splitByWords(cardData.category)}>
                  {cardData.category}
                </div>
                <small className="subtitle">Category</small>
              </div>
            </div>
            <div className="border-start border-vertical"></div>
            <div className="agent-info-item d-flex">
              <UserIcon className="user-icon me-2" />
              <div>
                <div className="app-info-text" title={splitByWords(cardData?.created_by)}>
                  {cardData?.created_by}
                </div>
                <small className="subtitle">Created by</small>
              </div>
            </div>
            {!isMobile && (
              <>
                <div className="border-start border-vertical"></div>
                <div className="agent-info-item d-flex">
                  <TermsOfUseIcon className="me-2 mt-1" />
                  <div>
                    <div className="app-info-text" title="App Usage Guidelines">
                      App Usage Guidelines
                    </div>
                    <Link
                      className="terms-of-use"
                      to={cardData?.terms_of_use}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Click Here
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Add condition for showing exmaple promts */}
          {cardData?.example_prompts?.length > 0 ? (
            <div className={`prompt-container ${isMobile && 'horizontal-alignments-mobile mb-1'}`}>
              <span className="prompt-text mb-3">Example prompts</span>

              <div>
                <div className="example-prompt-card-container p-0}">
                  {cardData?.example_prompts.map((prompt, index) => (
                    <div key={index} className="example-prompt-card" title={splitByWords(prompt)}>
                      <div className="d-flex example-prompt-card-body">
                        <small className="example-prompt-text">{prompt}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            ''
          )}
          {/* Add condition for showing exmaple promts */}

          {cardData.label !== 'Brand Agent' && cardData?.capabilities['web_search'] && (
            <div className={`capabilities ${isMobile && 'horizontal-alignments-mobile mb-0'}`}>
              <div className="capibility-text">Capabilities</div>
              <p>
                <span className="check-icon">
                  <CheckIcon className="me-2" />
                </span>
                <span>Web Search</span>
              </p>
            </div>
          )}

          <div className="d-md-none">
            <div className="app-builder-terms ms-sm-4 mb-4">
              Learn more about our
              <Link
                className="ms-1"
                to={cardData?.terms_of_use}
                target="_blank"
                rel="noopener noreferrer"
              >
                App Usage Guidelines
              </Link>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          className={`justify-content-between border-0 p-0 ${isMobile && 'horizontal-alignments-mobile'}`}
        >
          <Button onClick={() => setShowSummaryModal(false)} className="cancel-button m-0">
            Cancel
          </Button>

          {tabName !== 'in-app' && (
            <Button
              className="ms-auto me-2 light-without-border favourite-btn-icon"
              onClick={handleToggleFavourite}
              disabled={cardData?.agent_status != 'published'}
            >
              <span>{markFavourite ? <FilledHeartIcon /> : <EmptyHeartIcon />}</span>
            </Button>
          )}
          <Button
            ref={launchButtonRef}
            onKeyDown={handleLaunchKeyDown}
            onClick={handleLaunchApp}
            disabled={cardData?.agent_status != 'published' || tabName == 'in-app'}
            className="blue-without-border m-0"
          >
            Launch App
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Model Ends Here */}
    </>
  );
};
